import { Checkbox, TextInput, PasswordInput } from "@Core/Components/Inputs";
import InputError from "@Core/Components/InputError";
import InputLabel from "@Core/Components/InputLabel";
import GuestLayout from "@Core/Layouts/GuestLayout";
import { PrimaryButton, SecondaryButton } from "@Core/Components/Buttons";
import { Head, Link, router, useForm } from "@inertiajs/react";
import { FormEventHandler, useEffect, useState } from "react";
import backgroundImage from "@images/login-background.jpg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faClinicMedical,
    faPercentage,
    faUser,
    faUserTie,
} from "@fortawesome/free-solid-svg-icons";
import ConfirmOrChangeEmailModal from "@Passport/Components/Modals/ConfirmOrChangeEmailModal";
import { PharmacyRedirectModal } from "@/Portals/Passport/Components/Modals/PharmacyRedirectModal";
import { useTypedPageProps } from "@/Core/Hooks/useTypedPageProps";
import { LoginPageProps } from "@/Portals/Passport/Pages/types";

export default function Login() {
    const [showEmailModal, setShowEmailModal] = useState<boolean>(false);
    const [showPharmacyModal, setshowPharmacyModal] = useState<boolean>(false);

    const {
        flash,
        featureFlags,
        status,
        canResetPassword,
        selectedPortal,
        foundEmail,
        adminEmailRequired,
        redirectPharmacies,
    } = useTypedPageProps<LoginPageProps>();

    const { data, setData, post, processing, errors } = useForm({
        email: "",
        password: "",
        remember: false,
        portal: selectedPortal || "patient",
    });
    useEffect(() => {
        if (
            data.portal == "admin" &&
            adminEmailRequired != undefined &&
            (flash.message === "Please provide an email address" ||
                flash.message ===
                    "We found the following email attached to this account")
        ) {
            setData(
                "email",
                !data.email.includes("@") && foundEmail?.includes("@")
                    ? foundEmail
                    : !data.email.includes("@")
                      ? ""
                      : data.email,
            );
            setShowEmailModal(true);
        }
    }, [flash, foundEmail, adminEmailRequired]);

    const submit: FormEventHandler = (e) => {
        e.preventDefault();

        post(route("login.index"), {
            preserveState: true,
        });
    };

    return (
        <GuestLayout backgroundImagePath={backgroundImage}>
            <Head title="Log in" />

            {status && (
                <div className="mb-4 font-medium text-green-600 text-center">
                    {status}
                </div>
            )}

            <form onSubmit={submit} className="">
                <div className="mb-4">
                    <h1 className="text-2xl text-bold text-center">
                        Sav-Rx Passport
                    </h1>
                </div>

                <div className="mt-4">
                    <InputLabel htmlFor="portal-select" value="I am a..." />
                    <div
                        id="portal-select"
                        className="md:flex-row flex flex-col gap-2"
                    >
                        {featureFlags?.login_methods[
                            "patient-login-method"
                        ] && (
                            <PrimaryButton
                                data-dusk="i-am-a-patient-button"
                                className={`w-full `}
                                success={
                                    data.portal === "patient" ||
                                    data.portal === "mobile_app"
                                }
                                onClick={(e) => {
                                    e.preventDefault();
                                    setData("portal", "patient");
                                }}
                            >
                                <FontAwesomeIcon icon={faUser} size={"2x"} />
                                Patient
                            </PrimaryButton>
                        )}

                        {featureFlags?.login_methods["admin-login-method"] && (
                            <PrimaryButton
                                data-dusk="i-am-a-admin-button"
                                className={`w-full`}
                                success={data.portal === "admin"}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setData("portal", "admin");
                                }}
                            >
                                <FontAwesomeIcon icon={faUserTie} size={"2x"} />
                                Plan Administrator
                            </PrimaryButton>
                        )}

                        <PrimaryButton
                            data-dusk="i-am-a-provider-button"
                            className={`w-full`}
                            success={data.portal === "provider"}
                            onClick={(e) => {
                                e.preventDefault();
                                redirectPharmacies
                                    ? setshowPharmacyModal(true)
                                    : setData("portal", "provider");
                            }}
                        >
                            <FontAwesomeIcon
                                icon={faClinicMedical}
                                size={"2x"}
                            />
                            Pharmacy
                        </PrimaryButton>

                        {featureFlags?.login_methods["rxdc-login-method"] && (
                            <PrimaryButton
                                data-dusk="i-am-a-rxdc-button"
                                className={`w-full`}
                                success={data.portal === "rxdc"}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setData("portal", "rxdc");
                                }}
                            >
                                <FontAwesomeIcon
                                    icon={faPercentage}
                                    size={"2x"}
                                />
                                RxDC Administrator
                            </PrimaryButton>
                        )}
                    </div>
                    <InputError message={errors.portal} className="mt-2" />
                </div>

                <div className="mt-4">
                    <InputLabel htmlFor="email" value="Email" />
                    <TextInput
                        data-dusk="email-input"
                        id="email"
                        name="email"
                        className="mt-1 block w-full"
                        autoComplete="off"
                        isFocused={true}
                        onChange={(e) => setData("email", e.target.value)}
                    />

                    <InputError message={errors.email} className="mt-2" />
                </div>

                <div className="mt-4">
                    <InputLabel htmlFor="password" value="Password" />

                    <div className="flex">
                        <PasswordInput
                            password={data.password}
                            onChange={(e) =>
                                setData("password", e.target.value)
                            }
                        />
                    </div>
                    <InputError message={errors.password} className="mt-2" />
                </div>

                <div className="mt-4 flex items-center justify-between">
                    <label className="flex items-center">
                        <Checkbox
                            name="remember"
                            checked={data.remember}
                            onChange={(e) =>
                                setData("remember", e.target.checked)
                            }
                        />
                        <span className="ms-2 text-sm text-gray-600">
                            Stay signed in for 14 days
                        </span>
                    </label>
                    {canResetPassword && (
                        <Link
                            href={route("password.forgot.create")}
                            className="rounded-md text-sm text-gray-600 underline hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                        >
                            Forgot your password?
                        </Link>
                    )}
                </div>
                <div className="mt-2 text-center">
                    <PrimaryButton
                        data-dusk="login-button"
                        className="w-full justify-center"
                        disabled={processing}
                        type="submit"
                    >
                        Log in
                    </PrimaryButton>
                    <SecondaryButton
                        className="w-full justify-center mt-2 uppercase"
                        onClick={() => router.visit(route("register.index"))}
                    >
                        Don't Have a Sav-Rx Login? Click Here
                    </SecondaryButton>
                </div>
                <ConfirmOrChangeEmailModal
                    open={showEmailModal}
                    email={data.email}
                    error={errors?.email}
                    emailRequired={adminEmailRequired}
                    onChange={(email) => {
                        setData("email", email);
                    }}
                />
            </form>
            <PharmacyRedirectModal
                show={showPharmacyModal}
                onClose={() => {
                    setshowPharmacyModal(false);
                }}
            />
        </GuestLayout>
    );
}
